/* ---------------------------------------------------------------------------
   LAMOUR CUSTOMER REVIEWS — shared storefront renderer (Phase 4)
   ---------------------------------------------------------------------------
   Fetches approved reviews from the CustomerReviews Cloudflare Worker's public
   /api/reviews endpoint and renders them into any container that carries a
   [data-lamour-reviews] attribute. One script drives the home carousel, the
   product-page list, and the full /pages/reviews list.

   Container attributes (set by the Liquid sections):
     data-lamour-reviews            marker (required)
     data-endpoint="https://…"      worker origin (required)
     data-scope="home|all|product"  which query to run (default "all")
     data-product-id="gid://…"      required when scope="product"
     data-layout="carousel|list"    card layout (default "list")
     data-categories="huipils,pillows,textiles,featured"
                                    optional filter (any subset, from the
                                    section's checkboxes). Empty/absent = all.
                                    "featured" = only featured reviews; the
                                    others filter by product category, with
                                    uncategorised reviews always included.
     data-paginate="true"           add a Load-more button (scope="all")
     data-hide-when-empty="true"    remove the whole section if 0 reviews
                                    (used on product pages — one-off items
                                    almost never have their own review)

   Design rules:
   - Fail silent. Any fetch/JSON error leaves the page exactly as it was
     (never a broken block, matching image-retry.js's defensive posture).
   - The worker returns photo as a RELATIVE "/photo/<key>" path, an ABSOLUTE
     Shopify CDN URL (product-default fallback), or null. Relative paths get
     the endpoint origin prepended here.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var PAGE_SIZE = 20; // must match the worker's PUBLIC_PAGE_SIZE

  function esc(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Prepend the worker origin to a relative "/photo/..." path; pass through
  // absolute URLs (Shopify CDN fallbacks) and nulls.
  function resolvePhoto(endpoint, photo) {
    if (!photo) return null;
    if (/^https?:\/\//i.test(photo)) return photo;
    return endpoint.replace(/\/+$/, '') + photo;
  }

  function starsHtml(rating) {
    var r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += '<span class="lamour-review-star' + (i <= r ? ' is-filled' : '') + '">★</span>';
    }
    return '<span class="lamour-review-stars" role="img" aria-label="' + r + ' out of 5 stars">' + out + '</span>';
  }

  function formatDate(ms) {
    var n = Number(ms);
    if (!isFinite(n)) return '';
    try {
      return new Date(n).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return ''; }
  }

  function cardHtml(review, endpoint, layout) {
    var photo = resolvePhoto(endpoint, review.photo);
    var name = review.reviewerName ? esc(review.reviewerName) : 'Verified customer';
    var date = formatDate(review.createdAt);
    var title = review.title ? '<p class="lamour-review-title">' + esc(review.title) + '</p>' : '';
    var body = review.body ? '<p class="lamour-review-body">' + esc(review.body) + '</p>' : '';
    var img = photo
      ? '<div class="lamour-review-media"><img src="' + esc(photo) + '" loading="lazy" alt="" width="330" height="290"></div>'
      : '';

    // A product-linked review (rare for one-off items) links back to the product.
    var product = '';
    if (review.productHandle && review.productTitle) {
      product = '<a class="lamour-review-product" href="/products/' + esc(review.productHandle) + '">' + esc(review.productTitle) + '</a>';
    }

    return '' +
      '<article class="lamour-review-card lamour-review-card--' + esc(layout) + '">' +
        img +
        '<div class="lamour-review-content">' +
          starsHtml(review.rating) +
          title +
          body +
          '<p class="lamour-review-meta"><span class="lamour-review-name">' + name + '</span>' +
            (date ? '<span class="lamour-review-date">' + esc(date) + '</span>' : '') +
          '</p>' +
          product +
        '</div>' +
      '</article>';
  }

  function buildUrl(container, page) {
    var endpoint = container.getAttribute('data-endpoint').replace(/\/+$/, '');
    var scope = container.getAttribute('data-scope') || 'all';
    var url = endpoint + '/api/reviews?scope=' + encodeURIComponent(scope) + '&page=' + (page || 0);
    if (scope === 'product') {
      var pid = container.getAttribute('data-product-id') || '';
      url += '&product_id=' + encodeURIComponent(pid);
    }
    var cats = container.getAttribute('data-categories') || '';
    if (cats) url += '&categories=' + encodeURIComponent(cats);
    return url;
  }

  function render(container) {
    var endpoint = container.getAttribute('data-endpoint');
    if (!endpoint) return; // nothing to talk to
    var scope = container.getAttribute('data-scope') || 'all';
    if (scope === 'product' && !container.getAttribute('data-product-id')) {
      // No product id → nothing to show; honour hide-when-empty.
      if (container.getAttribute('data-hide-when-empty') === 'true') hideSection(container);
      return;
    }

    var layout = container.getAttribute('data-layout') || 'list';
    var paginate = container.getAttribute('data-paginate') === 'true';
    var listEl = container.querySelector('[data-lamour-reviews-list]') || container;
    var page = 0;

    function load(append) {
      fetch(buildUrl(container, page), { headers: { 'Accept': 'application/json' } })
        .then(function (res) { if (!res.ok) throw new Error('bad status ' + res.status); return res.json(); })
        .then(function (data) {
          var items = (data && data.items) || [];
          if (!append && items.length === 0) {
            if (container.getAttribute('data-hide-when-empty') === 'true') hideSection(container);
            return;
          }
          var html = items.map(function (r) { return cardHtml(r, endpoint, layout); }).join('');
          if (append) listEl.insertAdjacentHTML('beforeend', html);
          else listEl.innerHTML = html;

          container.setAttribute('data-lamour-ready', 'true');

          if (paginate) {
            var moreBtn = container.querySelector('[data-lamour-reviews-more]');
            if (moreBtn) {
              if (items.length < PAGE_SIZE) moreBtn.style.display = 'none';
              else moreBtn.style.display = '';
            }
          }
        })
        .catch(function () {
          // Fail silent: leave the page untouched. Hide the shell only if asked.
          if (!append && container.getAttribute('data-hide-when-empty') === 'true') hideSection(container);
        });
    }

    if (paginate) {
      var moreBtn = container.querySelector('[data-lamour-reviews-more]');
      if (moreBtn) {
        moreBtn.addEventListener('click', function () { page += 1; load(true); });
      }
    }

    load(false);
  }

  function hideSection(container) {
    var section = container.closest('.shopify-section') || container;
    section.style.display = 'none';
  }

  function init() {
    var nodes = document.querySelectorAll('[data-lamour-reviews]');
    for (var i = 0; i < nodes.length; i++) render(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor only: when Shopify re-draws a section in place (e.g. a
  // category checkbox toggled), it dispatches shopify:section:load on the
  // section wrapper WITHOUT a page reload — so init() never re-runs and the
  // fresh container sits empty until Save. Re-render any review containers in
  // the reloaded section so the new filter shows on the fly. This event fires
  // only inside the customizer, so the live storefront is unaffected.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e.target && e.target.querySelectorAll ? e.target : document;
    var nodes = scope.querySelectorAll('[data-lamour-reviews]');
    for (var i = 0; i < nodes.length; i++) render(nodes[i]);
  });
})();
